import { Header } from "@/components/Header";
import { PremiumFooter } from "@/components/landing/PremiumFooter";
import { Heart, Target, Sparkles, Users, Zap, Globe, MapPin, Mail, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const values = [
  {
    icon: Heart,
    title: "Passion Africaine",
    description: "Nés au Cameroun, nous sommes passionnés par l'innovation et la créativité africaine. Chaque fonctionnalité est conçue avec amour.",
  },
  {
    icon: Target,
    title: "Excellence",
    description: "Nous visons l'excellence dans tout ce que nous faisons. La qualité n'est jamais un compromis.",
  },
  {
    icon: Users,
    title: "Communauté",
    description: "Nos utilisateurs africains et du monde entier sont au cœur de nos décisions. Nous construisons Floop avec et pour vous.",
  },
  {
    icon: Zap,
    title: "Innovation Made in Africa",
    description: "Nous prouvons que l'Afrique peut être à la pointe de l'IA générative et de l'innovation technologique.",
  },
];

const team = [
  {
    name: "Équipe Fondatrice",
    role: "Direction & Vision",
    description: "Des entrepreneurs camerounais passionnés par l'IA et la créativité",
  },
  {
    name: "Équipe Technique",
    role: "Développement & IA",
    description: "Ingénieurs talentueux basés à Bertoua et dans la région",
  },
  {
    name: "Équipe Design",
    role: "UX & Créativité",
    description: "Designers créatifs qui rendent Floop fun et accessible",
  },
];

const milestones = [
  { year: "2023", event: "Fondation à Bertoua", description: "Début du développement de Floop au Cameroun" },
  { year: "2024 Q1", event: "Version Alpha", description: "Premiers tests avec des créateurs camerounais" },
  { year: "2024 Q2", event: "Version Beta", description: "Lancement de la beta en Afrique centrale" },
  { year: "2024 Q3", event: "Floop Pro", description: "Introduction du modèle Pro pour des résultats professionnels" },
  { year: "2024 Q4", event: "Lancement Afrique", description: "Ouverture au marché africain avec paiement Mobile Money" },
  { year: "2025", event: "Expansion", description: "Extension vers l'Afrique de l'Ouest et le reste du monde" },
];

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-16 text-center">
        <div className="container mx-auto px-6">
          <span className="inline-block text-sm font-medium text-primary mb-4">
            À propos
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-display">
            Notre mission :{" "}
            <span className="bg-gradient-floop bg-clip-text text-transparent">
              démocratiser la création
            </span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Floop est né d'une vision simple : rendre la création visuelle accessible à tous. 
            Grâce à notre modèle IA, nous permettons à chacun de donner vie à ses idées, 
            sans compétences techniques.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-6 font-display">
                Notre histoire 🇨🇲
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Floop est né à <strong className="text-foreground">Bertoua, dans la région de l'Est du Cameroun</strong>. 
                  Une équipe de jeunes entrepreneurs africains passionnés par l'intelligence artificielle et le design, 
                  convaincus que l'Afrique peut être à la pointe de l'innovation technologique.
                </p>
                <p>
                  Face à la complexité des outils existants et leur inaccessibilité pour le marché africain, 
                  nous avons créé Floop : une plateforme intuitive, puissante et accessible avec des tarifs 
                  adaptés au pouvoir d'achat local et des paiements en FCFA.
                </p>
                <p>
                  Aujourd'hui, Floop aide des milliers de créateurs, entrepreneurs et professionnels 
                  africains et du monde entier à donner vie à leurs idées visuelles chaque jour.
                </p>
              </div>
              
              {/* Location Card */}
              <div className="mt-6 bg-card border border-border rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  <span className="font-semibold text-foreground">Siège social</span>
                </div>
                <p className="text-sm text-muted-foreground">Bertoua, Région de l'Est, Cameroun</p>
                <div className="flex gap-4 mt-3">
                  <a href="mailto:Contact@floop.sbs" className="text-sm text-primary hover:underline flex items-center gap-1">
                    <Mail className="w-4 h-4" /> Contact@floop.sbs
                  </a>
                  <a href="https://wa.me/237697747365" className="text-sm text-green-500 hover:underline flex items-center gap-1">
                    <Phone className="w-4 h-4" /> WhatsApp
                  </a>
                </div>
              </div>
            </div>
            <div className="bg-gradient-floop/20 rounded-3xl p-8 text-center">
              <Sparkles className="w-16 h-16 text-primary mx-auto mb-6" />
              <div className="text-5xl font-bold text-foreground mb-2">100k+</div>
              <div className="text-muted-foreground">Images générées</div>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div>
                  <div className="text-2xl font-bold text-foreground">5k+</div>
                  <div className="text-sm text-muted-foreground">Utilisateurs</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">10+</div>
                  <div className="text-sm text-muted-foreground">Pays africains</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Team */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-display">
              Notre équipe
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Une équipe camerounaise passionnée qui construit le futur de la création visuelle en Afrique.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {team.map((member, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-2xl p-6 text-center hover:border-primary/50 transition-colors"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-floop/20 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-1">{member.name}</h3>
                <p className="text-sm text-primary mb-2">{member.role}</p>
                <p className="text-sm text-muted-foreground">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-display">
              Nos valeurs
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Les principes qui guident chacune de nos décisions.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-2xl p-6 text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{value.title}</h3>
                <p className="text-sm text-muted-foreground">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-display">
              Notre parcours
            </h2>
          </div>
          <div className="max-w-3xl mx-auto">
            <div className="space-y-6">
              {milestones.map((milestone, index) => (
                <div key={index} className="flex gap-6">
                  <div className="w-24 shrink-0 text-right">
                    <span className="text-sm font-semibold text-primary">{milestone.year}</span>
                  </div>
                  <div className="relative pb-6">
                    <div className="absolute left-0 top-2 w-3 h-3 rounded-full bg-primary" />
                    {index < milestones.length - 1 && (
                      <div className="absolute left-1.5 top-5 w-px h-full bg-border" />
                    )}
                    <div className="pl-6">
                      <h3 className="font-semibold text-foreground">{milestone.event}</h3>
                      <p className="text-sm text-muted-foreground">{milestone.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-6 text-center">
          <Globe className="w-16 h-16 text-primary mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-foreground mb-4 font-display">
            Rejoignez l'aventure Floop
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Nous recrutons des talents passionnés pour construire le futur de la création visuelle.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/careers">
              <Button size="lg" className="bg-gradient-floop hover:opacity-90 text-white rounded-full px-8">
                Voir les offres d'emploi
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="rounded-full px-8">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default About;
